import React, { Fragment, useMemo, useState } from "react";
import { OpenInNewTabIcon } from "assets";
import { useQuery } from "react-query";
import _unique from "lodash/uniq";
import _map from "lodash/map";
import _flatten from "lodash/flatten";
import _omit from "lodash/omit";
import _values from "lodash/values";
import _compact from "lodash/compact";
import _chunk from "lodash/chunk";
import { v4 as uuid } from "uuid";
import { useTranslation } from "i18n";
import { LocationService, GET_LOCATIONS_BY_ID } from "shared-services";
import { PaginationControl, Photo } from "component-library";
import { getPaginationResult } from "component-library/src/components/Functional/helpers/getPaginationResult";
import { buildPhotoLink } from "component-library/src/components/Functional/helpers/buildPhotoLink";
import {
  QuickViewCard,
  Card,
  getPerformersLocations,
} from "component-library/src/components/Organisms/Cards";
import { dataTestIds } from "data-testids";
import { IPerformer } from "shared/models/Performers";
import { PREMIUM_TAKE_COUNT } from "pages/AgentSearchPage/AgentSearchPage";
import config from "../../config/global";

import "./PerformerResults.scss";

const locationService = new LocationService({ url: config.locationsApiUrl });

interface IPerformerResultsProps {
  performers: IPerformer[];
  page: number;
  changePage: (page: number) => void;
  isNameAnchorDisabled?: boolean;
}

const ITEMS_PER_PAGE = 48;
const ITEMS_PER_ROW = 6;

function paginateResults(
  results: IPerformer[],
  pageSize: number,
  pageNumber: number
): { paginatedData: IPerformer[]; paginatedDataLength: number } {
  const paginatedData = results.slice(
    (pageNumber - 1) * pageSize,
    pageNumber * pageSize
  );

  return { paginatedData, paginatedDataLength: paginatedData.length };
}

// TODO: Combine the quick view card from here and casting into one reuseable component

export const PerformerResults = ({
  performers,
  page,
  changePage,
  isNameAnchorDisabled,
}: IPerformerResultsProps) => {
  const { t } = useTranslation();
  const [cardPosition, setCardPosition] = useState<
    | {
        x: number;
        y: number;
      }
    | undefined
  >();
  const maxResultsCount = Math.min(PREMIUM_TAKE_COUNT, performers.length);
  const locationIds = _unique(
    _map(_flatten(_map(performers, "locations")), "locationId")
  );
  const { data: locationsData = [] } = useQuery(
    [GET_LOCATIONS_BY_ID, locationIds],
    () => locationService.getGoogleLocationsByIds(locationIds)
  );

  const { paginatedData } = paginateResults(performers, ITEMS_PER_PAGE, page);

  const handleChangePage = (page: number) => {
    setCardPosition(undefined);
    changePage(page);
  };

  const renderPaginationControl = () => {
    return (
      <PaginationControl
        pageCount={Math.ceil(maxResultsCount / ITEMS_PER_PAGE)}
        pageRangeDisplayed={8}
        handlePageChange={handleChangePage}
        selectedPage={page}
      />
    );
  };

  const performersMatrix = _chunk(paginatedData, ITEMS_PER_ROW);

  const selectedPerformer = useMemo(() => {
    return cardPosition
      ? performersMatrix[cardPosition.y][cardPosition.x]
      : undefined;
  }, [performersMatrix, cardPosition]);

  const mainAgent = selectedPerformer?.mainAgent;

  const agentAddress = useMemo(
    () =>
      mainAgent?.address &&
      _compact(_values(_omit(mainAgent?.address, "id"))).join(", "),
    [mainAgent?.address]
  );

  const selectedPerformerLocations = useMemo(
    () =>
      getPerformersLocations(selectedPerformer?.locations || [], locationsData),
    [selectedPerformer?.locations, locationsData]
  );

  const getPerformerProfileUrl = (performer?: IPerformer) => {
    return performer?.viewPin
      ? `${config.siteRootUrl}${performer.viewPin}`
      : undefined;
  };
  const selectedPerformerProfileUrl = getPerformerProfileUrl(selectedPerformer);

  const selectedPerformerName = `${selectedPerformer?.forename} ${selectedPerformer?.surname}`;

  return (
    <>
      <div className="mx-auto max-w-[1440px] px-[15px]">
        <div className="mb-4 flex items-center justify-between">
          <div className="py-[15px] font-semibold">
            {getPaginationResult(page, ITEMS_PER_PAGE, maxResultsCount, {
              end:
                maxResultsCount < ITEMS_PER_PAGE * page
                  ? maxResultsCount
                  : page * ITEMS_PER_PAGE,
            })}
          </div>
          {renderPaginationControl()}
        </div>
      </div>
      <div className="grid break-after-avoid grid-cols-1 gap-[1.6rem] px-4 text-m sm:grid-cols-6 sm:gap-4">
        {performersMatrix.map((row, rowIndex) => {
          const isEndOfRow = cardPosition?.x === ITEMS_PER_ROW - 1;
          const isStartOfRow = cardPosition?.x === 0;
          const isStartOfMatrix =
            cardPosition?.y === 0 && cardPosition?.x === 0;
          const isEndOfMatrix =
            performersMatrix.length === (cardPosition?.y || 0) + 1 &&
            row.length - 1 === cardPosition?.x;

          return (
            <Fragment key={uuid()}>
              {row.map((performer, index) => {
                const profilePhoto = buildPhotoLink(
                  config.cloudinaryApi,
                  performer.mainPhoto?.fileName || ""
                );

                return (
                  <Card
                    key={performer.id}
                    fullName={`${performer.forename} ${performer.surname}`}
                    handleClick={() =>
                      setCardPosition({
                        x: index,
                        y: rowIndex,
                      })
                    }
                    membershipType={performer.performerType}
                    imageUrl={profilePhoto}
                    linkUrl={getPerformerProfileUrl(performer)}
                    className="mb-4 cursor-pointer"
                    isNameAnchorDisabled={isNameAnchorDisabled}
                  />
                );
              })}
              {cardPosition &&
                selectedPerformer &&
                rowIndex === cardPosition.y && (
                  <div
                    className="col-span-full"
                    data-testid={
                      dataTestIds.nuJobsApp[
                        "PerformerResultsPremium.PerformerResultsPremium.quickView"
                      ]
                    }
                  >
                    <QuickViewCard
                      handleClick={() => setCardPosition(undefined)}
                      noOfCards={ITEMS_PER_ROW}
                      selectedIndex={cardPosition.x + 1 || 0}
                      prevCardButtonAction={() =>
                        setCardPosition({
                          x: isStartOfRow
                            ? ITEMS_PER_ROW - 1
                            : cardPosition.x - 1,
                          y: isStartOfRow ? cardPosition.y - 1 : cardPosition.y,
                        })
                      }
                      nextCardButtonAction={() =>
                        setCardPosition({
                          x: isEndOfRow ? 0 : cardPosition.x + 1,
                          y: isEndOfRow ? cardPosition.y + 1 : cardPosition.y,
                        })
                      }
                      isNextCardAvailable={!isEndOfMatrix}
                      isPrevCardAvailable={!isStartOfMatrix}
                    >
                      <div className="px-[10px]">
                        <div className="grid grid-cols-12 p-[10px]">
                          <div className="col-span-full md:col-span-2 md:col-start-4">
                            {selectedPerformerProfileUrl &&
                            !isNameAnchorDisabled ? (
                              <a
                                href={selectedPerformerProfileUrl}
                                target="_blank"
                                className="c-quick-view-header__link"
                                rel="noreferrer"
                              >
                                <span className="c-quick-view-header__title">
                                  {selectedPerformerName}
                                </span>
                                <span className="c-quick-view-header__icon-font">
                                  <OpenInNewTabIcon />
                                </span>
                              </a>
                            ) : (
                              <div>{selectedPerformerName}</div>
                            )}
                          </div>
                        </div>
                        <div className="grid h-full grid-cols-12 gap-[1.1rem] overflow-auto bg-color-neutral-three pb-14 md:grid-rows-none lg:grid-rows-[auto_1fr]">
                          <div className="col-span-full row-span-full md:col-span-3">
                            <div className="col-span-full row-span-full md:col-span-3">
                              <div className="performer-results-photo">
                                <Photo
                                  src={buildPhotoLink(
                                    config.cloudinaryApi,
                                    selectedPerformer.mainPhoto?.fileName || ""
                                  )}
                                  className="sticky top-0"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="col-span-full grid grid-cols-5 py-3 md:col-span-9">
                            <div className="row-span-auto col-span-full p-[10px] md:col-span-3">
                              {selectedPerformerLocations.length > 0 && (
                                <div className="flex">
                                  <div className="w-full font-semibold">
                                    {t("common:label.locations.colon")}
                                  </div>
                                  <div className="w-full">
                                    {selectedPerformerLocations.map(
                                      (location) => (
                                        <div key={location}>{location}</div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                              <div className="flex">
                                <div className="w-full font-semibold">
                                  {t("common:label.playingAge.colon")}
                                </div>
                                <div className="w-full">{`${selectedPerformer.playingAgeFrom} - ${selectedPerformer.playingAgeTo} years`}</div>
                              </div>
                            </div>
                            <div className="row-span-max col-span-full px-[10px] md:col-span-2">
                              <div className="font-semibold">
                                {mainAgent?.name}
                              </div>
                              {agentAddress && (
                                <div className="text-[0.89rem]">
                                  {agentAddress}
                                </div>
                              )}
                              <div>
                                <a href={`tel:${mainAgent?.phone}`}>
                                  {mainAgent?.phone}
                                </a>
                              </div>
                              <div>
                                <a href={`mailto:${mainAgent?.email}`}>
                                  {mainAgent?.email}
                                </a>
                              </div>
                              <div>
                                <a
                                  href={`https://${mainAgent?.website}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {mainAgent?.website}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </QuickViewCard>
                  </div>
                )}
            </Fragment>
          );
        })}
      </div>
      <div className="mx-auto flex max-w-[1440px] justify-end px-[15px] pb-4">
        {renderPaginationControl()}
      </div>
    </>
  );
};
